import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchGames, fetchOverview, fetchLifecycleStats } from '../api/client';
import SignalBadge from '../components/SignalBadge';
import DataTable from '../components/DataTable';
import { DataFreshnessPanel } from '../components/FreshnessIndicator';
import type { GameSummary } from '../types';

type SortOption = 'commence_time' | 'signal_count' | 'aggregate_strength';

export default function Games() {
  const [sportFilter, setSportFilter] = useState('');
  const [betTypeFilter, setBetTypeFilter] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('commence_time');
  const [signalsOnly, setSignalsOnly] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['games', { sport: sportFilter, betType: betTypeFilter, sort: sortBy, signalsOnly }],
    queryFn: () => fetchGames({
      sport: sportFilter || undefined,
      betType: betTypeFilter || undefined,
      sort: sortBy,
      sortDir: sortBy === 'commence_time' ? 'asc' : 'desc',
      signalsOnly,
    }),
    refetchInterval: 30000,
  });

  const { data: overview } = useQuery({
    queryKey: ['overview'],
    queryFn: fetchOverview,
    refetchInterval: 30000,
  });

  const { data: lifecycleStats } = useQuery({
    queryKey: ['lifecycle-stats'],
    queryFn: fetchLifecycleStats,
    refetchInterval: 30000,
  });

  const columns = [
    {
      key: 'status',
      header: 'Status',
      render: (game: GameSummary) => (
        game.isLive ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-semibold animate-pulse">
            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
            LIVE
          </span>
        ) : (
          <span className="text-xs text-gray-500">-</span>
        )
      ),
    },
    {
      key: 'pollingTier',
      header: 'Starts',
      render: (game: GameSummary) => {
        const tier = getPollingTier(game.commenceTime, game.isLive);
        return (
          <div className="flex flex-col">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${tier.color} ${tier.bg}`}>
              {tier.icon && <span className={tier.icon === '●' ? 'animate-pulse' : ''}>{tier.icon}</span>}
              {tier.timeLabel || tier.label}
            </span>
            {tier.timeLabel && (
              <span className="text-[10px] text-gray-500 mt-0.5 pl-0.5">
                {tier.label === 'SOON' ? '5 min poll' : '15 min poll'}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'sport',
      header: 'Sport',
      render: (game: GameSummary) => (
        <span className="text-xs text-gray-400 uppercase tracking-wide">
          {formatSport(game.sport)}
        </span>
      ),
    },
    {
      key: 'teams',
      header: 'Matchup',
      render: (game: GameSummary) => (
        <div>
          <div className="font-medium flex items-center gap-2">
            {game.homeTeam} vs {game.awayTeam}
            {game.isLive && (
              <span className="inline-flex w-2 h-2 bg-red-500 rounded-full animate-pulse" title="Live"></span>
            )}
          </div>
          <div className="text-xs text-gray-400">
            {formatDateTime(game.commenceTime)}
          </div>
        </div>
      ),
    },
    {
      key: 'signals',
      header: 'Signals',
      render: (game: GameSummary) => (
        <div className="flex items-center gap-2">
          <span className={`text-lg font-bold ${game.signalCount > 0 ? 'text-accent' : 'text-gray-500'}`}>
            {game.signalCount}
          </span>
          {Object.keys(game.signalTypeBreakdown).length > 0 && (
            <div className="flex gap-1">
              {Object.entries(game.signalTypeBreakdown).slice(0, 3).map(([type, count]) => (
                <div key={type} className="flex items-center">
                  <SignalBadge type={type} showLabel={false} size="sm" />
                  {(count as number) > 1 && (
                    <span className="text-xs text-gray-500 ml-0.5">x{count as number}</span>
                  )}
                </div>
              ))}
              {Object.keys(game.signalTypeBreakdown).length > 3 && (
                <span className="text-xs text-gray-500">+{Object.keys(game.signalTypeBreakdown).length - 3}</span>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'strength',
      header: 'Strength',
      render: (game: GameSummary) => (
        <div className="flex items-center gap-2">
          <div className="w-16 h-2 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-accent transition-all"
              style={{ width: `${game.aggregateStrength * 100}%` }}
            />
          </div>
          <span className="text-sm text-gray-300 w-10">
            {(game.aggregateStrength * 100).toFixed(0)}%
          </span>
        </div>
      ),
    },
    {
      key: 'direction',
      header: 'Direction',
      render: (game: GameSummary) => <DirectionBadge direction={game.dominantDirection} />,
    },
    {
      key: 'polymarket',
      header: 'PM Match',
      render: (game: GameSummary) => (
        game.polymarket ? (
          <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs">
            {(game.polymarket.matchingConfidence * 100).toFixed(0)}%
          </span>
        ) : (
          <span className="text-gray-500 text-xs">-</span>
        )
      ),
    },
    {
      key: 'betType',
      header: 'Market',
      render: (game: GameSummary) => (
        game.polymarket?.betType ? (
          <BetTypeBadge type={game.polymarket.betType} />
        ) : (
          <span className="text-gray-500 text-xs">-</span>
        )
      ),
    },
    {
      key: 'opportunities',
      header: 'Opps',
      render: (game: GameSummary) => (
        game.opportunityCount > 0 ? (
          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs font-medium">
            {game.opportunityCount}
          </span>
        ) : (
          <span className="text-gray-500 text-xs">-</span>
        )
      ),
    },
    {
      key: 'action',
      header: '',
      render: (game: GameSummary) => (
        <Link
          to={`/events/${game.eventId}`}
          className="px-3 py-1.5 bg-accent/20 text-accent rounded-md text-sm font-medium hover:bg-accent/30 transition-colors whitespace-nowrap"
        >
          View Event
        </Link>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Games</h1>
        <div className="flex items-center gap-4">
          <DataFreshnessPanel
            sources={[
              { label: 'Odds', lastUpdate: overview?.lastUpdates.odds },
              { label: 'Polymarket', lastUpdate: overview?.lastUpdates.polymarket },
            ]}
            warningThreshold={360}
            staleThreshold={600}
          />
          <span className="text-sm text-gray-400">
            {data?.games.length || 0} {signalsOnly ? 'games with signals' : 'upcoming games'}
          </span>
        </div>
      </div>

      {/* Lifecycle Stats Panel */}
      {lifecycleStats && (
        <div className="card">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">⏱️</span>
            <h2 className="text-sm font-semibold text-gray-300">Lifecycle & Polling Overview</h2>
            <span className="ml-auto text-xs text-gray-500 bg-gray-800 px-2 py-1 rounded">
              UTC
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {/* Polling Tiers */}
            <div className="bg-surface-50 rounded-lg p-3">
              <div className="text-xs text-gray-400 uppercase mb-1">Later (&gt;2h)</div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                <span className="text-lg font-bold text-blue-400">{lifecycleStats.pollingTiers.later}</span>
                <span className="text-xs text-gray-500">15 min</span>
              </div>
            </div>
            <div className="bg-surface-50 rounded-lg p-3">
              <div className="text-xs text-gray-400 uppercase mb-1">Soon (&lt;2h)</div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                <span className="text-lg font-bold text-yellow-400">{lifecycleStats.pollingTiers.soon}</span>
                <span className="text-xs text-gray-500">5 min</span>
              </div>
            </div>
            <div className="bg-surface-50 rounded-lg p-3">
              <div className="text-xs text-gray-400 uppercase mb-1">Live</div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                <span className="text-lg font-bold text-red-400">{lifecycleStats.pollingTiers.live}</span>
                <span className="text-xs text-gray-500">2 min</span>
              </div>
            </div>
            {/* Historical Data Import Status */}
            <div className="bg-surface-50 rounded-lg p-3">
              <div className="text-xs text-gray-400 uppercase mb-1">Odds History</div>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${lifecycleStats.historicalData.oddsApi.percentage >= 80 ? 'bg-green-500' : lifecycleStats.historicalData.oddsApi.percentage >= 50 ? 'bg-yellow-500' : 'bg-orange-500'}`}></span>
                <span className={`text-lg font-bold ${lifecycleStats.historicalData.oddsApi.percentage >= 80 ? 'text-green-400' : lifecycleStats.historicalData.oddsApi.percentage >= 50 ? 'text-yellow-400' : 'text-orange-400'}`}>
                  {lifecycleStats.historicalData.oddsApi.percentage}%
                </span>
                <span className="text-xs text-gray-500">imported</span>
              </div>
            </div>
            <div className="bg-surface-50 rounded-lg p-3">
              <div className="text-xs text-gray-400 uppercase mb-1">PM History</div>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${lifecycleStats.historicalData.polymarket.percentage >= 80 ? 'bg-green-500' : lifecycleStats.historicalData.polymarket.percentage >= 50 ? 'bg-yellow-500' : 'bg-orange-500'}`}></span>
                <span className={`text-lg font-bold ${lifecycleStats.historicalData.polymarket.percentage >= 80 ? 'text-green-400' : lifecycleStats.historicalData.polymarket.percentage >= 50 ? 'text-yellow-400' : 'text-orange-400'}`}>
                  {lifecycleStats.historicalData.polymarket.percentage}%
                </span>
                <span className="text-xs text-gray-500">imported</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 flex-wrap items-center">
        <select
          value={sportFilter}
          onChange={(e) => setSportFilter(e.target.value)}
          className="bg-surface-50 border border-gray-700 rounded-md px-3 py-2 text-sm"
        >
          <option value="">All Sports</option>
          {data?.sports.map((sport) => (
            <option key={sport} value={sport}>
              {formatSport(sport)}
            </option>
          ))}
        </select>

        <select
          value={betTypeFilter}
          onChange={(e) => setBetTypeFilter(e.target.value)}
          className="bg-surface-50 border border-gray-700 rounded-md px-3 py-2 text-sm"
        >
          <option value="">All Bet Types</option>
          <option value="moneyline">Moneyline</option>
          <option value="spread">Spread</option>
          <option value="total">Total</option>
          <option value="player_points">Player Points</option>
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortOption)}
          className="bg-surface-50 border border-gray-700 rounded-md px-3 py-2 text-sm"
        >
          <option value="commence_time">Sort by Game Time</option>
          <option value="signal_count">Sort by Signal Count</option>
          <option value="aggregate_strength">Sort by Signal Strength</option>
        </select>

        {/* Signals Only Toggle */}
        <label className="flex items-center gap-2 cursor-pointer">
          <div className="relative">
            <input
              type="checkbox"
              checked={signalsOnly}
              onChange={(e) => setSignalsOnly(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:bg-accent transition-colors"></div>
            <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
          </div>
          <span className="text-sm text-gray-300">Signals only</span>
        </label>
      </div>

      {/* Games Table */}
      <DataTable
        columns={columns}
        data={data?.games || []}
        keyExtractor={(game) => `${game.eventId}-${game.polymarket?.eventId || 'no-pm'}`}
        loading={isLoading}
        emptyMessage={signalsOnly ? 'No games with active signals' : 'No upcoming games found'}
      />
    </div>
  );
}

function DirectionBadge({ direction }: { direction: string }) {
  const config: Record<string, { icon: string; color: string; bg: string }> = {
    home: { icon: '←', color: 'text-blue-400', bg: 'bg-blue-500/20' },
    away: { icon: '→', color: 'text-red-400', bg: 'bg-red-500/20' },
    over: { icon: '↑', color: 'text-green-400', bg: 'bg-green-500/20' },
    under: { icon: '↓', color: 'text-orange-400', bg: 'bg-orange-500/20' },
    mixed: { icon: '↔', color: 'text-gray-400', bg: 'bg-gray-500/20' },
  };
  const { icon, color, bg } = config[direction] || config.mixed;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${color} ${bg}`}>
      <span>{icon}</span>
      <span className="capitalize">{direction}</span>
    </span>
  );
}

function BetTypeBadge({ type }: { type: string }) {
  const config: Record<string, { label: string; color: string; bg: string }> = {
    moneyline: { label: 'H2H', color: 'text-blue-400', bg: 'bg-blue-500/20' },
    spread: { label: 'Spread', color: 'text-purple-400', bg: 'bg-purple-500/20' },
    total: { label: 'O/U', color: 'text-orange-400', bg: 'bg-orange-500/20' },
    player_points: { label: 'Props', color: 'text-green-400', bg: 'bg-green-500/20' },
  };
  const { label, color, bg } = config[type] || { label: type, color: 'text-gray-400', bg: 'bg-gray-500/20' };

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${color} ${bg}`}>
      {label}
    </span>
  );
}

function formatSport(sport: string): string {
  const parts = sport.split('_');
  if (parts.length >= 2) {
    return parts[parts.length - 1].toUpperCase();
  }
  return sport.toUpperCase();
}

function formatDateTime(isoString: string): string {
  return new Date(isoString).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getPollingTier(commenceTime: string, isLive: boolean): { label: string; color: string; bg: string; icon: string; timeLabel?: string } {
  if (isLive) {
    return { label: 'LIVE', color: 'text-red-400', bg: 'bg-red-500/20', icon: '●' };
  }

  const now = new Date();
  const start = new Date(commenceTime);
  const msUntilStart = start.getTime() - now.getTime();
  const hoursUntilStart = msUntilStart / (1000 * 60 * 60);

  if (hoursUntilStart <= 0) {
    // Game should have started - likely live
    return { label: 'LIVE', color: 'text-red-400', bg: 'bg-red-500/20', icon: '●' };
  }

  // Calculate time label
  const timeLabel = formatTimeUntil(msUntilStart);

  if (hoursUntilStart <= 2) {
    return { label: 'SOON', color: 'text-yellow-400', bg: 'bg-yellow-500/20', icon: '◐', timeLabel };
  }

  return { label: 'LATER', color: 'text-blue-400', bg: 'bg-blue-500/20', icon: '○', timeLabel };
}

function formatTimeUntil(ms: number): string {
  const totalMinutes = Math.floor(ms / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    if (remainingHours === 0) {
      return `in ${days}d`;
    }
    return `in ${days}d ${remainingHours}h`;
  }

  if (hours > 0) {
    if (minutes === 0) {
      return `in ${hours}h`;
    }
    return `in ${hours}h ${minutes}m`;
  }

  return `in ${minutes}m`;
}
